import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';

import './user.scss';

import {
  BtnBar,
  Checkbox,
  DangerAlert,
  FormItem,
  FormRow,
  SecondaryBtn,
  WaitingAlert,
} from '@tmapy/style-guide';

import type { UserRetrieveResponse } from '../../openapi/types';
import { useUsersRetrieveResource } from '../../openapi/operations/usersRetrieve';
import { useUserRolesListResource } from '../../openapi/operations/userRolesList';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { addressMessages } from '../../app/components/AddressAutocomplete';
import { QUERY_STATE } from '../../constants';
import type { UseResource } from '../../hooks/useResource';
import { useAuth } from '../../hooks';

import { userMessages, userQueryMessages } from './userMessages';

type UserProps = {
  resource?: UseResource<UserRetrieveResponse>;
  userId: string | number;
  onCloseDialog?(): void;
};

export const User: React.FC<UserProps> = ({ userId, onCloseDialog }) => {
  const resource = useUsersRetrieveResource(userId ? { id: userId as number } : null);

  const formatMessage = useMessage();
  const auth = useAuth();

  const loadQueryState = resource.queryState;
  const data = (resource.data ?? {}) as UserRetrieveResponse;

  const availableRoles = useUserRolesListResource({}, true);

  const roles = resource.data?.roles ?? [];
  const isRoleInputVisible =
    availableRoles && auth.hasPermission('Users', 'update.role', resource.data);

  // RENDER
  return (
    <MainContent>
      <article className='sg-u-vs-4'>
        <PageTitle>
          <FormattedMessage {...userMessages.title} />
        </PageTitle>
        {loadQueryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...userQueryMessages.loadQueryStateWaiting} />
          </WaitingAlert>
        )}
        {loadQueryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...userQueryMessages.loadQueryStateFail} />
          </DangerAlert>
        )}
        {loadQueryState === QUERY_STATE.SUCCESS && (
          <div className='sg-u-vs-2'>
            <div className='sg-u-box sg-a-p-2 sg-u-vs-2'>
              <FormRow>
                <FormItem label={formatMessage(userMessages.nameLabel)}>{data.name}</FormItem>
                <FormItem label={formatMessage(userMessages.surnameLabel)}>{data.surname}</FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(addressMessages.street)}>{data.street}</FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(addressMessages.city)}>{data.city}</FormItem>
                <FormItem label={formatMessage(addressMessages.psc)}>{data.postal_code}</FormItem>
              </FormRow>

              <FormRow>
                <FormItem label={formatMessage(userMessages.emailLabel)}>{data.email}</FormItem>
                <FormItem label={formatMessage(userMessages.phoneLabel)}>{data.phone}</FormItem>
              </FormRow>
              <FormRow>
                <FormItem label={formatMessage(userMessages.icoLabel)}>{data.ico}</FormItem>
                <FormItem label={formatMessage(userMessages.organizationLabel)}>
                  {data.company_name}
                </FormItem>
              </FormRow>

              <FormRow>
                <FormItem label={formatMessage(userMessages.accountNameLabel)}>
                  {data.account_name}
                </FormItem>
                <FormItem label={formatMessage(userMessages.accountTypeLabel)}>
                  {data.account_type}
                </FormItem>
              </FormRow>

              {isRoleInputVisible && (
                <FormRow>
                  <FormItem label={formatMessage(userMessages.rolesLabel)}>
                    <div className='dmx-user--roleRow'>
                      {availableRoles.data?.results?.map((item) => (
                        <Checkbox
                          key={item.url}
                          name={item.url}
                          isChecked={!!roles.find((role) => role === item.url)}
                          isDisabled
                        >
                          {item.label}
                        </Checkbox>
                      ))}
                    </div>
                  </FormItem>
                </FormRow>
              )}

              <FormRow>
                <FormItem label={formatMessage(userMessages.noteLabel)}>{data.note}</FormItem>
              </FormRow>
            </div>
            {onCloseDialog && (
              <BtnBar>
                <SecondaryBtn isDanger onClick={onCloseDialog}>
                  <FormattedMessage {...userMessages.btnCloseDialog} />
                </SecondaryBtn>
              </BtnBar>
            )}
          </div>
        )}
      </article>
    </MainContent>
  );
};
